import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useTranslate } from '@tolgee/react';
import SearchMenu from "../GenericComponents/SearchMenu";
import DateRangePicker from "../GenericComponents/DateRangePicker";
import DateSeparator from "../Chat/DateSeparator";
import { RotatingLines } from "react-loader-spinner";
import { Repeat } from "lucide-react";
import { colors } from "../../Assets/styles/colors";
import MeetingListElement from "./MeetingListElement";
import { UPCOMING_DATE_PRESETS } from "../../Helpers/dateTimeHelper";
import { groupMeetingsByDay, formatDayGroupLabel, hasUnmaterializedOccurrences } from "../../Helpers/recurrenceHelper";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

import InfiniteScroll from "react-infinite-scroll-component";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import EmptyMeetingList from "../AudioVideoCalls/EmptyMeetingList";

const MeetingList = ({
  meetings,
  handleJoinMeeting,
  selectedMeeting,
  handleSelectMeeting,
  loadMeetings,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [dateFilter, setDateFilter] = useState({ dateFrom: null, dateTo: null });
  const debouncedSearch = useDebouncedValue(searchInput);
  const { meetingListSpinner } = useGenericComponentsStateService();
  const { t } = useTranslate();

  const isFiltered = !!debouncedSearch || (!!dateFilter.dateFrom && !!dateFilter.dateTo);

  useEffect(() => {
    loadMeetings({ q: debouncedSearch, dateFrom: dateFilter.dateFrom, dateTo: dateFilter.dateTo });
  }, [debouncedSearch, dateFilter]);

  const groupedMeetings = groupMeetingsByDay(meetings);
  const showMoreOccurrencesNotice = !isFiltered && hasUnmaterializedOccurrences(meetings);

  const hasEventOnDate = (date) => {
    const key = moment(date).format("YYYY-MM-DD");
    return meetings.some((meeting) => moment(meeting.start_at).format("YYYY-MM-DD") === key);
  };

  const handleDateFilterChange = (dateFrom, dateTo) => {
    setDateFilter({ dateFrom, dateTo });
  };

  return (
    <ChatsList>
      <FiltersRow>
        <SearchMenu value={searchInput} setvalue={setSearchInput} />
        <DateRangePicker
          presets={UPCOMING_DATE_PRESETS}
          disabledDirection="past"
          dayHasEventFn={hasEventOnDate}
          onChange={handleDateFilterChange}
        />
      </FiltersRow>

      {!meetingListSpinner ? (
        groupedMeetings.length > 0 ? (
          <Scrollwrapper id={"meetingsList"}>
            <InfiniteScroll
              dataLength={meetings.length}
              scrollableTarget="meetingsList" // This must have `overflow: auto`
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%",
              }}
            >
              {groupedMeetings.map((group) => (
                <React.Fragment key={group.key}>
                  <div id={`meeting-date-${group.key}`}>
                    <DateSeparator date={formatDayGroupLabel(t, group.date)} />
                  </div>
                  {group.meetings.map((meeting) => (
                    <MeetingListElement
                      key={meeting.id}
                      data={meeting}
                      selected={selectedMeeting?.id === meeting.id}
                      handleJoinMeeting={handleJoinMeeting}
                      handleSelectMeeting={handleSelectMeeting}
                    />
                  ))}
                </React.Fragment>
              ))}
            </InfiniteScroll>
            {showMoreOccurrencesNotice && (
              <MoreOccurrencesNotice>
                <NoticeIcon size={13} />
                {t('meetings.more_occurrences_notice')}
              </MoreOccurrencesNotice>
            )}
          </Scrollwrapper>
        ) : (
          <EmptyMeetingList messageKey={isFiltered ? "meetings.jump_to_date_no_results" : "meetings.meeting_empty_list"} />
        )
      ) : (
        <SpinnerWrapper>
          <RotatingLines
            strokeColor={colors.purple}
            strokeWidth={3}
            animationDuration="0.75"
            width={50}
            visible={true}
          />
        </SpinnerWrapper>
      )}
    </ChatsList>
  );
};

export default MeetingList;
const ChatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  width: 100%;
  overflow: hidden;
  padding: 16px 0;
`;
const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 16px;
`;
const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
const Scrollwrapper = styled.div`
  height: 100%;
  overflow: auto; /* Required */
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 100%;
  position: relative;
`;
const MoreOccurrencesNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 16px 0 16px;
  padding: 12px 0;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
const NoticeIcon = styled(Repeat)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;
